import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createEngine,
  getCloudApiUrl,
  getCloudToken,
  setCloudApiUrl,
  setCloudToken,
  clearCloudAuth,
  setApplyChangeCallback,
} from '../lib/sync-engine-desktop';
import { SyncEngine } from '@coreledger/sync-engine';
import { apiFetch } from '../api/client';

const IS_DESKTOP = !!import.meta.env.VITE_API_URL;
const POLL_MS = 30_000;

interface SyncState {
  isConnected: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: string | null;
  pendingCount: number;
  conflictCount: number;
}

interface CloudSyncHook extends SyncState {
  connectToCloud: (cloudUrl: string, email: string, password: string) => Promise<void>;
  sync: () => Promise<void>;
  disconnect: () => void;
}

export function useCloudSync(): CloudSyncHook {
  const engineRef = useRef<SyncEngine | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [state, setState] = useState<SyncState>(() => ({
    isConnected: IS_DESKTOP && !!getCloudApiUrl() && !!getCloudToken(),
    isSyncing: false,
    lastSyncAt: null,
    syncError: null,
    pendingCount: 0,
    conflictCount: 0,
  }));

  // Refresh the pending ops count from the local embedded API
  const refreshPendingCount = useCallback(async () => {
    if (!IS_DESKTOP) return;
    try {
      const ops = await apiFetch<unknown[]>('/sync/local/pending-ops');
      setState((s) => ({ ...s, pendingCount: ops.length }));
    } catch {
      // non-critical
    }
  }, []);

  // Initialise engine from persisted credentials on mount
  useEffect(() => {
    if (!IS_DESKTOP) return;
    const url = getCloudApiUrl();
    if (url && getCloudToken()) {
      engineRef.current = createEngine(url, 'Desktop');
      refreshPendingCount();
    }
  }, [refreshPendingCount]);

  // Poll pending count every 30 s while connected
  useEffect(() => {
    if (!state.isConnected) return;
    pollRef.current = setInterval(refreshPendingCount, POLL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [state.isConnected, refreshPendingCount]);

  const connectToCloud = useCallback(async (cloudUrl: string, email: string, password: string) => {
    setState((s) => ({ ...s, syncError: null, isSyncing: true }));
    try {
      // Authenticate against the cloud API
      const trimmedUrl = cloudUrl.replace(/\/$/, '');
      const loginRes = await fetch(`${trimmedUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!loginRes.ok) {
        const body = await loginRes.json().catch(() => ({})) as { message?: string };
        throw new Error(body.message ?? `Login failed (${loginRes.status})`);
      }
      const { accessToken } = await loginRes.json() as { accessToken: string };

      setCloudApiUrl(trimmedUrl);
      setCloudToken(accessToken);

      const engine = createEngine(trimmedUrl, 'Desktop');
      engineRef.current = engine;
      await engine.connect();

      setState((s) => ({ ...s, isConnected: true, isSyncing: false }));
      await refreshPendingCount();
    } catch (err) {
      clearCloudAuth();
      engineRef.current = null;
      setState((s) => ({ ...s, isConnected: false, isSyncing: false, syncError: (err as Error).message }));
    }
  }, [refreshPendingCount]);

  const sync = useCallback(async () => {
    if (!engineRef.current) return;
    setState((s) => ({ ...s, isSyncing: true, syncError: null, conflictCount: 0 }));
    try {
      // Capture pending entityIds before push to detect pull conflicts
      const prePending = await apiFetch<{ entityId: string }[]>('/sync/local/pending-ops');
      const pendingEntityIds = new Set(prePending.map((op) => op.entityId));

      let conflicts = 0;
      setApplyChangeCallback((entityId) => {
        if (pendingEntityIds.has(entityId)) conflicts++;
      });

      await engineRef.current.sync();

      setApplyChangeCallback(null);
      setState((s) => ({ ...s, isSyncing: false, lastSyncAt: new Date(), conflictCount: conflicts }));
      await refreshPendingCount();
    } catch (err) {
      setApplyChangeCallback(null);
      setState((s) => ({ ...s, isSyncing: false, syncError: (err as Error).message }));
    }
  }, [refreshPendingCount]);

  const disconnect = useCallback(() => {
    clearCloudAuth();
    engineRef.current = null;
    if (pollRef.current) clearInterval(pollRef.current);
    setState({ isConnected: false, isSyncing: false, lastSyncAt: null, syncError: null, pendingCount: 0, conflictCount: 0 });
  }, []);

  return { ...state, connectToCloud, sync, disconnect };
}
